import { Button, Result } from 'antd';
import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router';

import { NO_ACCESS_PATH, homePath } from '@/app/navigation';
import { FullScreenSpin } from '@/components/FullScreenSpin';
import { ru } from '@/i18n/ru';

import { useAuth, type Role } from './authContext';

interface Props {
  roles: readonly Role[];
  children: ReactNode;
  /** What to show when the session cannot be restored for want of a network. */
  offline?: ReactNode;
}

/** Renders children for signed-in users with one of `roles`; redirects everyone else. */
export function RequireAuth({ roles, children, offline }: Props) {
  const { state, retryRestore } = useAuth();
  const location = useLocation();

  switch (state.status) {
    case 'loading':
      return <FullScreenSpin />;
    case 'offline':
      if (offline) return offline;
      return (
        <Result
          status="warning"
          title={ru.session.restoreFailed}
          extra={
            <Button type="primary" onClick={retryRestore}>
              {ru.config.retry}
            </Button>
          }
        />
      );
    case 'anonymous':
      return <Navigate to="/login" replace state={{ from: location.pathname }} />;
    case 'authenticated': {
      if (roles.includes(state.user.role)) return children;
      const home = homePath(state.user.role);
      // Never back to where we are: that loop is a blank page.
      if (home === location.pathname) return <Navigate to={NO_ACCESS_PATH} replace />;
      return <Navigate to={home} replace />;
    }
  }
}
